

















import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import FileManager from "@/components/files/FileManager.vue";
import { BaseFile as IBaseFile } from "@/api-v2/files/models/base-file";
import { i18n } from "@/locales/settings";

import tinymce from "tinymce";
import ru from "@/plugins/tinymce/ru.js";
import "tinymce/themes/silver/theme";
import "tinymce/icons/default";
import "tinymce/plugins/image";
import "tinymce/plugins/table";
import "tinymce/plugins/media";
import "tinymce/plugins/hr";
import "tinymce/plugins/link";
import "tinymce/plugins/lists";
import "tinymce/plugins/imagetools";
import "tinymce/plugins/autolink";
import "tinymce/plugins/autoresize";
import "tinymce/plugins/visualblocks";
import "tinymce/plugins/pagebreak";
import "tinymce/plugins/fullscreen";
import "tinymce/plugins/code";
import "tinymce/plugins/paste";

import "tinymce/skins/ui/oxide/skin.min.css";

interface MyBaseFile extends IBaseFile {
  title: string;
  mimeType: string;
  url: string;
}

@Component({
  components: { FileManager }
})
export default class EditorWithFile extends Vue {
  @Prop() value?: string;
  @Prop() placeholder?: string;
  @Prop({
    default: () =>
      "id" +
      Math.random()
        .toString(16)
        .slice(2)
  })
  id?: string;
  @Prop({ default: true }) showNotImages?: boolean;
  @Prop({ default: true }) showFileManager?: boolean;
  @Prop({ default: false }) showOnlyImages?: boolean;
  @Prop({ default: 400 }) minHeight?: number;
  @Prop({ default: false }) inModal?: boolean;
  @Prop() defaultDisk?: string;
  body!: string;
  showFiles: boolean = false;
  editor: any = tinymce;

  data() {
    return {
      body: ""
    };
  }

  get localization(): string | null {
    const locale = localStorage?.getItem("localization");
    const en = locale === "en-EN";
    const ru = locale === "ru-RU";

    const navLanguage = window.navigator.language === "ru-RU" ? "ru" : "en";

    if (en) return "en";
    if (ru) return "ru";
    return navLanguage;
  }

  getFile(items: Array<any>) {
    this.body = this.body ?? "";
    items.forEach(item => {
      if (item?.url) {
        if (item.mimeType.includes("image")) {
          this.addContentToEditor(
            `<img class="quill-custom-img" src="${item.url}" alt="${item.title}" data-file-id="${item.id}">`
          );
        } else if (item.mimeType.includes("video")) {
          this.addContentToEditor(
            `<video controls data-file-id="${item.id}" src="${item.url}"></video>`
          );
        } else {
          this.addContentToEditor(
            `<div><a href="${item.url}" target="_blank" data-file-id="${item.id}">${item.title}</a></div>`
          );
        }
      }
    });
  }

  get toolbar() {
    return this.showFileManager
      ? "undo redo | styleselect | fontsizeselect | bold italic strikethrough  forecolor backcolor | " +
          "alignleft aligncenter alignright alignjustify numlist bullist | link customImage media |table outdent indent hr blockquote | " +
          "removeformat pagebreak code fullscreen"
      : "undo redo | styleselect | fontsizeselect | bold italic strikethrough  forecolor backcolor | " +
          "alignleft aligncenter alignright alignjustify numlist bullist | table outdent indent hr blockquote | " +
          "link media | removeformat pagebreak code fullscreen";
  }

  initEditor(editor: any, vm: any) {
    editor.init({
      selector: `#${vm.id}`,
      theme: "silver",
      min_height: vm.minHeight,
      tiny_pageembed_inline_styles: true,
      language_url: ru,
      toolbar_mode: "sliding",
      relative_urls: false,
      remove_script_host: false,
      allow_script_urls: true,
      convert_urls: false,
      toolbar_sticky: true,
      fullscreen_native: true,
      language: vm.localization,
      menu: {
        custom: {
          title: `${i18n.tc(this.$translate.Paste)}`,
          items: "link media hr"
        }
      },
      menubar: "file edit view custom format table fullscreen",
      plugins:
        "code image table media mediaembed hr link lists imagetools autolink autoresize visualblocks pagebreak fullscreen paste",
      toolbar: vm.toolbar,
      paste_data_images: false,
      content_style:
        `@import url('https://fonts.googleapis.com/css2?family=Noto+Sans&display=swap');
          body { font-family: Noto Sans, sans-serif; }
      ` +
        `.mce-offscreen-selection { position: absolute } .mce-offscreen-selection { left: -2000000px }` +
        "h1, h2, h3, h4, h5, h6, .h1, .h2, .h3, .h4, .h5, .h6 { font-weight: 500 }  ",
      setup: (editor: any) => {
        editor.on("Change", function() {
          vm.body = editor.getContent();
        });
        editor.on("keyup", function() {
          vm.body = editor.getContent();
        });
        editor.ui.registry.addButton("customImage", {
          icon: "image",
          onAction: () => {
            vm.showFiles = !vm.showFiles;
          }
        });
        editor.on("init", function() {
          vm.initValue();
        });
      }
    });
  }

  addContentToEditor(val: any) {
    this.editor.get(this.id).insertContent(val);
    this.body = this.editor.get(this.id).getContent();
  }

  destroyEditor(editor: any) {
    editor.activeEditor.destroy();
  }

  beforeDestroy() {
    this.destroyEditor(this.editor);
  }

  @Watch("body")
  updateBody() {
    this.$emit("input", this.body);
  }

  @Watch("$route")
  updateEditor() {
    this.initEditor(this.editor, this);
  }

  @Watch("value")
  update() {
    if (this.value !== this.editor.get(this.id).getContent()) {
      this.editor.get(this.id).setContent(this.value);
    }
  }

  initValue() {
    this.editor.get(this.id).setContent(this.value);
  }

  mounted() {
    if (this.value) {
      this.body = this.value;
    }
    this.initEditor(this.editor, this);
  }
}
